import Axios, { CancelTokenSource } from "axios";

import { API } from "utils/api-config";

import { defaultErrorResponse } from "api/defaults";

import {
    DataImport,
    EntitySetting,
} from "store/environments/tenants/data-import/data-import-types";
import { Tenant } from "store/environments/environment-types";

export const loadDataImports = async (
    environmentId: string,
    tenantId: string,
    cancelTokenSource?: CancelTokenSource
): Promise<[Tenant<ReadonlyArray<DataImport>> | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.get(
        `/environments/${environmentId}/tenants/${tenantId}/data-import`,
        { cancelToken: cancelTokenSource?.token }
    );

    if (!cancelTokenSource?.token.reason) {
        if (!response) {
            return defaultErrorResponse;
        }

        return [response.data, response.status];
    } else {
        throw new Axios.Cancel();
    }
};

export const loadDataImportSettings = async (
    environmentId: string,
    tenantId: string,
    entity: string,
    cancelTokenSource?: CancelTokenSource
): Promise<[Record<string, EntitySetting> | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.get(
        `/environments/${environmentId}/tenants/${tenantId}/data-import/${entity}/settings`,
        { cancelToken: cancelTokenSource?.token }
    );

    if (!cancelTokenSource?.token.reason) {
        if (!response) {
            return defaultErrorResponse;
        }

        return [response.data, response.status];
    } else {
        throw new Axios.Cancel();
    }
};

export const updateDataImports = async (
    environmentId: string,
    tenantId: string,
    dataImports: DataImport[]
): Promise<[string | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.put(
        `/environments/${environmentId}/tenants/${tenantId}/data-import`,
        dataImports
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const updateDataImportSettings = async (
    environmentId: string,
    tenantId: string,
    entity: string,
    settings: Record<string, EntitySetting>
): Promise<[string | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.put(
        `/environments/${environmentId}/tenants/${tenantId}/data-import/${entity}/settings`,
        settings
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const transferAllCefData = async (
    environmentId: string,
    tenantId: string,
    entity: string
): Promise<[string | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.post(
        `/environments/${environmentId}/tenants/${tenantId}/data-import/${entity}/cef/transfer-data`
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const runAllAxImports = async (
    environmentId: string,
    tenantId: string,
    dataImports: DataImport[]
): Promise<[string | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }
    const response = await API.post(
        `/environments/${environmentId}/tenants/${tenantId}/data-import/ax/run-all`,
        dataImports
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const runAllCefEntities = async (
    environmentId: string,
    tenantId: string,
    dataImports: DataImport[]
): Promise<[string | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }
    const response = await API.post(
        `/environments/${environmentId}/tenants/${tenantId}/data-import/cef/run-all`,
        dataImports
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};
